import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import Button from '../../components/reusable/button/button.component';
import Spinner from '../../components/reusable/spinner/spinner.component';
import ComponentModal from '../../components/reusable/component-modal/component-modal.component';
import AdminModal from '../../components/reusable/admin-modal/admin-modal.component';
import NoteDisplay from '../../components/notes/note-display/note-display.component';
import NoteCard from '../../components/notes/note-card/note-card.component';
import AddNote from '../../components/notes/add-note/add-note.component';
import UpdateNote from '../../components/notes/update-note/update-note.component';

import Client from '../../tools/client.js';
import Tools from '../../tools/tools.js';

import {
  ButtonContainer,
  ContactInfoContainer,
  ContactInfoMessage,
  ContactInfoText,
  DeleteMessageButton,
  MainContainer,
  MessageContainer,
  MessageDataContainer,
  MessageTitle,
  SectionContainer,
  Field
} from './message.styles';

import { BackLink, Subtitle } from '../../styles/page.styles';

const client = new Client();
const tools = new Tools();

const MessagePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showNoteDisplayModal, setShowNoteDisplayModal] = useState(false);
  const [showNoteEditModal, setShowNoteEditModal] = useState(false);
  const [title, setTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [action, setAction] = useState(null);
  const [actionText, setActionText] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);

  useEffect(() => {
    const setMessageToReadStatus = async () => {
      await client.updateMessage({ id, status: 'read' });
      await getMessage();
    };
    setMessageToReadStatus();
  }, [id]);

  const getMessage = async () => {
    setLoading(true);
    const res = await client.getMessageById(id);
    setMessage(res);
    setLoading(false);
  };

  const onRefresh = async () => {
    const refreshed = await client.getMessageById(id);
    setMessage(refreshed);
    const refreshedNote = refreshed.MessageNotes?.find(entry => entry.Note.id === selectedNote?.id)?.Note;
    if (refreshedNote) setSelectedNote({ ...refreshedNote });
  };

  const markMessageReplied = async () => {
    await client.updateMessage({ id, replied: !message.replied });
    await getMessage();
  };

  const updateMessageStatus = async () => {
    const status = message.status === 'pending' ? 'read' : 'pending';
    await client.updateMessage({ id, status });
    await getMessage();
  };

  const softDeleteItem = async () => {
    await client.updateMessage({ id, deleted: !message.deleted });
    await getMessage();
  };

  const meta = message?.metadata || {};
  const fullName = `${meta.firstName || ''} ${meta.lastName || ''}`.trim() || 'Anonymous';

  return (
    <>
      <BackLink onClick={() => navigate('/messages')}>Back</BackLink>
      <MainContainer>
        <MessageTitle>Message</MessageTitle>
        {loading ? (
          <Spinner />
        ) : message ? (
          <>
            <MessageDataContainer>
              <ContactInfoContainer>
                <ContactInfoText>Name: {fullName}</ContactInfoText>
                <ContactInfoText>Phone: {meta.phone || 'N/A'}</ContactInfoText>
                <ContactInfoText>Email: {meta.email || 'N/A'}</ContactInfoText>
              </ContactInfoContainer>
              <ContactInfoContainer>
                <ContactInfoText>Date: {tools.formatCreatedAtDate(message.createdAt)}</ContactInfoText>
                <ContactInfoText>Status: {message.status}</ContactInfoText>
                <ContactInfoText>Replied: {message.replied ? 'Yes' : 'No'}</ContactInfoText>
              </ContactInfoContainer>
            </MessageDataContainer>

            <MessageContainer>
              <ContactInfoMessage>{message.message}</ContactInfoMessage>
            </MessageContainer>

            <ButtonContainer>
              <Button onClick={markMessageReplied}>{message.replied ? 'Undo Replied' : 'Mark Replied'}</Button>
              <Button onClick={updateMessageStatus}>{message.status === 'pending' ? 'Mark Read' : 'Mark New'}</Button>
              <Button onClick={() => setShowAddNoteModal(true)}>Add Note</Button>
            </ButtonContainer>

            <DeleteMessageButton
              color={message.deleted ? 'green' : 'red'}
              onClick={softDeleteItem}
            >
              {message.deleted ? 'Restore' : 'DELETE'}
            </DeleteMessageButton>

            {message.MessageNotes?.length > 0 && (
              <SectionContainer>
                <MessageTitle>Message Notes</MessageTitle>
                {message.MessageNotes.map((entry, idx) => (
                  <NoteCard
                    key={idx}
                    note={entry.Note}
                    createdAt={entry.createdAt}
                    onClick={() => {
                      setSelectedNote(entry.Note);
                      setShowNoteDisplayModal(true);
                    }}
                  />
                ))}
              </SectionContainer>
            )}

            <ComponentModal isOpen={showAddNoteModal} onClose={() => setShowAddNoteModal(false)} title="Add Note">
              <AddNote
                onSubmit={async (formData) => {
                  formData.append('messageId', message.id);
                  await client.createMessageNote(formData);
                  await getMessage();
                  setShowAddNoteModal(false);
                }}
              />
            </ComponentModal>

            <ComponentModal isOpen={showNoteDisplayModal} onClose={() => setShowNoteDisplayModal(false)} title="Message Note">
              <NoteDisplay
                note={selectedNote}
                setShowEdit={() => {
                  setShowNoteDisplayModal(false);
                  setShowNoteEditModal(true);
                }}
                onRefresh={onRefresh}
              />
            </ComponentModal>

            <ComponentModal isOpen={showNoteEditModal} onClose={() => setShowNoteEditModal(false)} title="Edit Note">
              <UpdateNote
                note={selectedNote}
                onUpdate={async (noteId, updatedData) => {
                  await client.updateNote(noteId, updatedData);
                  await getMessage();
                  setShowNoteEditModal(false);
                }}
                onDelete={async (noteId) => {
                  await client.deleteMessageNote(message.id, noteId);
                  await getMessage();
                  setShowNoteEditModal(false);
                }}
                onClose={() => setShowNoteEditModal(false)}
              />
            </ComponentModal>
          </>
        ) : (
          <MessageTitle>No Message To Display</MessageTitle>
        )}
      </MainContainer>
    </>
  );
};

export default MessagePage;